import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  activePinIdAtom,
  escapeHatchTriggerAtom,
  firstPinPlacedAtAtom,
  pinsAtom,
  pinsWithErrorsAtom,
  type EscapeHatchTriggerType
} from "@design-lab/entities/pinning";
import {
  trackPinSessionEscapeHatchClicked,
  trackPinSessionEscapeHatchConfirmed,
  trackPinSessionEscapeHatchDismissed,
  trackPinSessionEscapeHatchShown,
  type EscapeHatchEventOptions
} from "@design-lab/shared/lib/analytics";

const ESCAPE_HATCH_DELAY_MS = 3 * 60 * 1000;

type UseEscapeHatchParams = {
  onConfirm: () => void;
};

export function useEscapeHatch({ onConfirm }: UseEscapeHatchParams) {
  const [triggerType, setTriggerType] = useAtom(escapeHatchTriggerAtom);
  const [firstPinPlacedAt, setFirstPinPlacedAt] = useAtom(firstPinPlacedAtAtom);
  const pins = useAtomValue(pinsAtom);
  const activePinId = useAtomValue(activePinIdAtom);
  const setPinsWithErrors = useSetAtom(pinsWithErrorsAtom);

  const [escapeHatchModalOpen, setEscapeHatchModalOpen] = useState(false);
  const shownTrackedRef = useRef(false);

  const activePin = pins.find(p => p.id === activePinId);

  const buildEventOptions = useCallback(
    (trigger: EscapeHatchTriggerType): EscapeHatchEventOptions => ({
      triggerType: trigger,
      pinCount: pins.length,
      messageCount: activePin?.messages.length ?? 0
    }),
    [pins.length, activePin?.messages.length]
  );

  // Record timestamp when first pin is placed
  useEffect(() => {
    if (pins.length > 0 && firstPinPlacedAt == null) {
      setFirstPinPlacedAt(Date.now());
    }
  }, [pins.length, firstPinPlacedAt, setFirstPinPlacedAt]);

  // Condition C: Timer-based safety net (3 min after first pin)
  useEffect(() => {
    if (firstPinPlacedAt == null || triggerType != null) {
      return undefined;
    }

    const elapsed = Date.now() - firstPinPlacedAt;
    const remaining = ESCAPE_HATCH_DELAY_MS - elapsed;

    if (remaining <= 0) {
      setTriggerType(prev => prev ?? "timer");
      return undefined;
    }

    const timer = setTimeout(() => {
      setTriggerType(prev => prev ?? "timer");
    }, remaining);

    return () => clearTimeout(timer);
  }, [firstPinPlacedAt, triggerType, setTriggerType]);

  // Track "shown" event exactly once when trigger first fires
  useEffect(() => {
    if (triggerType != null && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      trackPinSessionEscapeHatchShown(buildEventOptions(triggerType));
    }
  }, [triggerType, buildEventOptions]);

  // Reset atoms on unmount
  useEffect(
    () => () => {
      setTriggerType(null);
      setPinsWithErrors(new Set());
      setFirstPinPlacedAt(null);
      shownTrackedRef.current = false;
    },
    [setTriggerType, setPinsWithErrors, setFirstPinPlacedAt]
  );

  const handleEscapeHatchClick = useCallback(() => {
    if (triggerType == null) return;
    trackPinSessionEscapeHatchClicked(buildEventOptions(triggerType));
    setEscapeHatchModalOpen(true);
  }, [triggerType, buildEventOptions]);

  const handleEscapeHatchConfirm = useCallback(() => {
    if (triggerType == null) return;
    trackPinSessionEscapeHatchConfirmed(buildEventOptions(triggerType));
    onConfirm();
  }, [triggerType, buildEventOptions, onConfirm]);

  const handleEscapeHatchDismiss = useCallback(() => {
    if (triggerType == null) return;
    trackPinSessionEscapeHatchDismissed(buildEventOptions(triggerType));
    setEscapeHatchModalOpen(false);
  }, [triggerType, buildEventOptions]);

  return {
    showEscapeHatch: triggerType != null,
    escapeHatchModalOpen,
    setEscapeHatchModalOpen,
    handleEscapeHatchClick,
    handleEscapeHatchConfirm,
    handleEscapeHatchDismiss
  };
}
